import type { ElectricalDevice } from '../model/types';

/**
 * Terminal anahtarı formatı: "deviceId:terminalId"
 */
export function makeTerminalKey(deviceId: string, terminalId: string): string {
  return `${deviceId}:${terminalId}`;
}

export function parseTerminalKey(key: string): { deviceId: string; terminalId: string } {
  const parts = key.split(':');
  return { deviceId: parts[0] ?? '', terminalId: parts[1] ?? '' };
}

/**
 * Union-Find (Disjoint-Set) Veri Yapısı — Eşpotansiyel İletken Kümelerini Bulmak İçin
 */
export class DisjointSet {
  private parent = new Map<string, string>();
  private rank = new Map<string, number>();

  public find(x: string): string {
    if (!this.parent.has(x)) {
      this.parent.set(x, x);
      this.rank.set(x, 0);
      return x;
    }
    const p = this.parent.get(x)!;
    if (p !== x) {
      const root = this.find(p);
      this.parent.set(x, root);
      return root;
    }
    return x;
  }

  public union(a: string, b: string): void {
    const rootA = this.find(a);
    const rootB = this.find(b);
    if (rootA === rootB) return;

    const rankA = this.rank.get(rootA) || 0;
    const rankB = this.rank.get(rootB) || 0;

    if (rankA < rankB) {
      this.parent.set(rootA, rootB);
    } else if (rankA > rankB) {
      this.parent.set(rootB, rootA);
    } else {
      this.parent.set(rootB, rootA);
      this.rank.set(rootA, rankA + 1);
    }
  }

  public getAllSets(): Map<string, Set<string>> {
    const sets = new Map<string, Set<string>>();
    for (const key of this.parent.keys()) {
      const root = this.find(key);
      if (!sets.has(root)) {
        sets.set(root, new Set());
      }
      sets.get(root)!.add(key);
    }
    return sets;
  }
}

export interface InternalConductionBridge {
  readonly terminalA: string;
  readonly terminalB: string;
  readonly isClosed: boolean;
}

/**
 * Her cihazın o anki iç anahtar/kontak iletim köprülerini döner
 */
export function getDeviceInternalBridges(
  device: ElectricalDevice,
  contactsClosed: Record<string, boolean>
): InternalConductionBridge[] {
  const bridges: InternalConductionBridge[] = [];
  const type = device.type;
  const props = device.props || {};

  switch (type) {
    case 'mcb-3p': {
      const isClosed = !props['tripped'] && props['closed'] !== false;
      bridges.push(
        { terminalA: makeTerminalKey(device.id, '1'), terminalB: makeTerminalKey(device.id, '2'), isClosed },
        { terminalA: makeTerminalKey(device.id, '3'), terminalB: makeTerminalKey(device.id, '4'), isClosed },
        { terminalA: makeTerminalKey(device.id, '5'), terminalB: makeTerminalKey(device.id, '6'), isClosed }
      );
      break;
    }

    case 'mcb-1p': {
      const isClosed = !props['tripped'] && props['closed'] !== false;
      bridges.push(
        { terminalA: makeTerminalKey(device.id, '1'), terminalB: makeTerminalKey(device.id, '2'), isClosed }
      );
      break;
    }

    case 'contactor-3p': {
      // Kontaktör bobini enerjili olduğunda kontaklar kapanır
      const mainClosed = contactsClosed['main'] ?? false;
      const auxNoClosed = contactsClosed['13-14'] ?? false;
      const auxNcClosed = contactsClosed['21-22'] ?? true;

      // 3 Ana güç kontağı
      bridges.push(
        { terminalA: makeTerminalKey(device.id, '1'), terminalB: makeTerminalKey(device.id, '2'), isClosed: mainClosed },
        { terminalA: makeTerminalKey(device.id, '3'), terminalB: makeTerminalKey(device.id, '4'), isClosed: mainClosed },
        { terminalA: makeTerminalKey(device.id, '5'), terminalB: makeTerminalKey(device.id, '6'), isClosed: mainClosed }
      );
      // Mühürleme kontağı (NO 13-14)
      bridges.push(
        { terminalA: makeTerminalKey(device.id, '13'), terminalB: makeTerminalKey(device.id, '14'), isClosed: auxNoClosed }
      );
      // Kapalı yardımcı kontak (NC 21-22)
      bridges.push(
        { terminalA: makeTerminalKey(device.id, '21'), terminalB: makeTerminalKey(device.id, '22'), isClosed: auxNcClosed }
      );
      break;
    }

    case 'thermal-relay': {
      const isTripped = props['tripped'] === true;
      // Ana akım yolu bimetal üzerinden her zaman iletir
      bridges.push(
        { terminalA: makeTerminalKey(device.id, '1'), terminalB: makeTerminalKey(device.id, '2'), isClosed: true },
        { terminalA: makeTerminalKey(device.id, '3'), terminalB: makeTerminalKey(device.id, '4'), isClosed: true },
        { terminalA: makeTerminalKey(device.id, '5'), terminalB: makeTerminalKey(device.id, '6'), isClosed: true }
      );
      // 95-96 Kumanda koruma kontağı (Normalde Kapalı, trip edince AÇILIR)
      bridges.push(
        { terminalA: makeTerminalKey(device.id, '95'), terminalB: makeTerminalKey(device.id, '96'), isClosed: !isTripped }
      );
      // 97-98 Arıza ihbar kontağı (Normalde Açık, trip edince KAPANIR)
      bridges.push(
        { terminalA: makeTerminalKey(device.id, '97'), terminalB: makeTerminalKey(device.id, '98'), isClosed: isTripped }
      );
      break;
    }

    case 'pushbutton-nc': {
      // Stop butonu: normalde iletir (NC), basılınca devreyi keser
      const isPressed = props['pressed'] === true;
      bridges.push(
        { terminalA: makeTerminalKey(device.id, '1'), terminalB: makeTerminalKey(device.id, '2'), isClosed: !isPressed }
      );
      break;
    }

    case 'pushbutton-no': {
      // Start butonu: normalde açık (NO), basılınca iletir
      const isPressed = props['pressed'] === true;
      bridges.push(
        { terminalA: makeTerminalKey(device.id, '3'), terminalB: makeTerminalKey(device.id, '4'), isClosed: isPressed }
      );
      break;
    }

    case 'power-source':
    case 'motor-3phase':
    case 'pilot-lamp':
    case 'terminal-block':
      // Bu elemanlar doğrudan sıfır dirençli kısa devre köprüsü oluşturmaz (yük veya kaynak)
      break;
  }

  return bridges;
}
